import { useState } from "react";
import axios from "axios";
import { API } from "../App";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Checkbox } from "./ui/checkbox";
import { toast } from "sonner";
import { CalendarRange, Plus, CheckCircle2, Loader2, Info } from "lucide-react";

const nextYearLabel = (jy) => {
  const m = /^(\d{4})-(\d{4})$/.exec(jy || "");
  return m ? `${+m[1] + 1}-${+m[2] + 1}` : "";
};

/** Φίλτρο δικαστικού έτους (15/9 → 14/9) + δημιουργία νέου έτους / νέου τομέα — user Νοέ 2026 */
export const JudicialYearBar = ({ jy, judicialYear, onChange, courtType, isAdmin, onRefresh }) => {
  const [newOpen, setNewOpen] = useState(false);
  const [sectorOpen, setSectorOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ judicial_year: "", copy_people: false, activate: false });
  const [sectorForm, setSectorForm] = useState({ sector_number: "", title: "", short_title: "", subjects: "" });
  const isActive = judicialYear === jy?.active;

  const openNew = () => { setForm({ judicial_year: nextYearLabel(jy?.active), copy_people: false, activate: false }); setNewOpen(true); };

  const createYear = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/judicial-years`, { ...form, copy_from: judicialYear });
      toast.success(`Δημιουργήθηκε το δικαστικό έτος ${r.data.judicial_year} (${r.data.sectors} τομείς)${form.activate ? " και ενεργοποιήθηκε" : ""}`);
      setNewOpen(false);
      await onRefresh();
      onChange(r.data.judicial_year);
    } catch (e) { toast.error(e.response?.data?.detail || "Σφάλμα δημιουργίας"); }
    finally { setBusy(false); }
  };

  const activate = async () => {
    if (!window.confirm(`Ενεργοποίηση δικαστικού έτους ${judicialYear}; Οι νέες πράξεις θα χρησιμοποιούν τους τομείς/συνθέσεις αυτού του έτους.`)) return;
    setBusy(true);
    try {
      await axios.post(`${API}/judicial-years/${judicialYear}/activate`);
      toast.success(`Ενεργό δικαστικό έτος: ${judicialYear}`);
      await onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Σφάλμα ενεργοποίησης"); }
    finally { setBusy(false); }
  };

  const createSector = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/judicial-years/${judicialYear}/sectors`, {
        court_type: courtType, sector_number: Number(sectorForm.sector_number), title: sectorForm.title, short_title: sectorForm.short_title,
        subjects: sectorForm.subjects.split(",").map(s => s.trim()).filter(Boolean),
      });
      toast.success(`Ο τομέας ${sectorForm.sector_number} δημιουργήθηκε για το ${judicialYear}`);
      setSectorOpen(false);
      setSectorForm({ sector_number: "", title: "", short_title: "", subjects: "" });
      await onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Σφάλμα δημιουργίας τομέα"); }
    finally { setBusy(false); }
  };

  return (
    <div className="flex items-end gap-3 flex-wrap" data-testid="jy-bar">
      <div className="min-w-[200px]">
        <Label className="text-xs flex items-center gap-1"><CalendarRange className="w-3 h-3" />Δικαστικό έτος (15/9 → 14/9)</Label>
        <Select value={judicialYear || ""} onValueChange={onChange}>
          <SelectTrigger data-testid="jy-select"><SelectValue placeholder="Δικαστικό έτος" /></SelectTrigger>
          <SelectContent>
            {(jy?.years || []).map(y => (
              <SelectItem key={y} value={y} data-testid={`jy-option-${y}`}>{y}{y === jy?.active ? " · ενεργό" : ""}{y === jy?.current && y !== jy?.active ? " · τρέχον" : ""}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {isActive
        ? <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200" data-testid="jy-active-badge"><CheckCircle2 className="w-3 h-3 mr-1" />Ενεργό έτος — χρησιμοποιείται στις νέες πράξεις</Badge>
        : <Badge variant="outline" className="border-amber-300 text-amber-800 bg-amber-50" data-testid="jy-history-badge"><Info className="w-3 h-3 mr-1" />Ιστορικό έτος — αλλαγές επηρεάζουν μόνο επανεκδόσεις πράξεων αυτού του έτους</Badge>}
      {isAdmin && (
        <div className="flex items-center gap-2 ml-auto">
          {!isActive && <Button size="sm" variant="outline" onClick={activate} disabled={busy} data-testid="jy-activate-btn">Ενεργοποίηση έτους</Button>}
          <Button size="sm" variant="outline" onClick={() => setSectorOpen(true)} disabled={busy} data-testid="jy-new-sector-btn"><Plus className="w-4 h-4 mr-1" />Νέος Τομέας</Button>
          <Button size="sm" onClick={openNew} disabled={busy} data-testid="jy-new-year-btn"><Plus className="w-4 h-4 mr-1" />Νέο δικαστικό έτος</Button>
        </div>
      )}

      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent data-testid="jy-new-year-dialog">
          <DialogHeader>
            <DialogTitle>Νέο δικαστικό έτος</DialogTitle>
            <DialogDescription>Αντιγράφεται η δομή τομέων (τίτλοι, αντικείμενα, πινάκια) από το {judicialYear}. Πρόεδροι/Συνθέσεις αντιγράφονται μόνο αν το επιλέξετε.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Δικαστικό έτος (ΕΕΕΕ-ΕΕΕΕ)</Label>
              <Input value={form.judicial_year} onChange={e => setForm(f => ({ ...f, judicial_year: e.target.value }))} placeholder="2027-2028" data-testid="jy-new-year-input" />
            </div>
            <label className="flex items-center gap-2 text-sm"><Checkbox checked={form.copy_people} onCheckedChange={v => setForm(f => ({ ...f, copy_people: !!v }))} data-testid="jy-copy-people" />Αντιγραφή Προέδρων Προσδιορισμού & Συνθέσεων</label>
            <label className="flex items-center gap-2 text-sm"><Checkbox checked={form.activate} onCheckedChange={v => setForm(f => ({ ...f, activate: !!v }))} data-testid="jy-activate-now" />Ενεργοποίηση αμέσως (νέες πράξεις με αυτό το έτος)</label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewOpen(false)}>Άκυρο</Button>
            <Button onClick={createYear} disabled={busy || !/^\d{4}-\d{4}$/.test(form.judicial_year)} data-testid="jy-new-year-submit">{busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Δημιουργία</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={sectorOpen} onOpenChange={setSectorOpen}>
        <DialogContent data-testid="jy-new-sector-dialog">
          <DialogHeader>
            <DialogTitle>Νέος Τομέας — {courtType} · {judicialYear}</DialogTitle>
            <DialogDescription>Ο τομέας δημιουργείται μόνο για το επιλεγμένο δικαστικό έτος.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Αριθμός</Label><Input type="number" value={sectorForm.sector_number} onChange={e => setSectorForm(f => ({ ...f, sector_number: e.target.value }))} data-testid="jy-sector-number" /></div>
            <div><Label className="text-xs">Σύντομος τίτλος</Label><Input value={sectorForm.short_title} onChange={e => setSectorForm(f => ({ ...f, short_title: e.target.value }))} data-testid="jy-sector-short" /></div>
            <div className="col-span-2"><Label className="text-xs">Τίτλος</Label><Input value={sectorForm.title} onChange={e => setSectorForm(f => ({ ...f, title: e.target.value }))} data-testid="jy-sector-title" /></div>
            <div className="col-span-2"><Label className="text-xs">Αντικείμενα (χωρισμένα με κόμμα)</Label><Input value={sectorForm.subjects} onChange={e => setSectorForm(f => ({ ...f, subjects: e.target.value }))} data-testid="jy-sector-subjects" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSectorOpen(false)}>Άκυρο</Button>
            <Button onClick={createSector} disabled={busy || !sectorForm.sector_number || !sectorForm.title} data-testid="jy-new-sector-submit">{busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Δημιουργία</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
